import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Checkbox, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { DynamicIcon } from 'src/components';
import { CustomViewAttribute } from 'src/graphql/entities/custom-view';
import { capitalizeString } from 'src/utils/strings';

const DraggableItem = ({
  id,
  column,
  onSelect,
}: {
  id: string;
  column: CustomViewAttribute;
  onSelect: (column: CustomViewAttribute) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable(
    { id },
  );

  const sx = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} sx={sx}>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        marginX="0.5rem"
      >
        <Stack flexDirection="row" alignItems="center">
          <Box {...attributes} {...listeners} sx={{ cursor: 'pointer' }}>
            <DynamicIcon name="drag_indicator" />
          </Box>

          <Checkbox checked onChange={() => onSelect(column)} />

          <Box maxWidth="15rem">
            <Typography
              variant="body2"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
            >
              {capitalizeString(column.attributeLabel)}
            </Typography>
          </Box>
        </Stack>
        <Typography variant="body2">{capitalizeString(column.group)}</Typography>
      </Stack>
    </Box>
  );
};

export const DraggableList = ({
  selectedColumns,
  onSelect,
  onReorder,
}: {
  selectedColumns: CustomViewAttribute[];
  onSelect: (column: CustomViewAttribute) => void;
  onReorder: (columns: CustomViewAttribute[]) => void;
}) => {
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = selectedColumns.findIndex((col) => col.attribute === active.id);

        const newIndex = selectedColumns.findIndex((col) => col.attribute === over?.id);

        onReorder(arrayMove(selectedColumns, oldIndex, newIndex));
      }
    },
    [selectedColumns, onReorder],
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const attributeList = useMemo(
    () => selectedColumns.map((col) => col.attribute),
    [selectedColumns],
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={attributeList} strategy={verticalListSortingStrategy}>
        {selectedColumns.map((column) => (
          <DraggableItem
            key={column.attribute}
            id={column.attribute}
            column={column}
            onSelect={onSelect}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};
